import { useState, useMemo, useCallback } from "react";
import { useGetAuditLogsQuery } from "@api/auditLog.api";
import {
    AuditLogFilter,
    AuditLogSourceType,
    AuditLog,
} from "@common/types/auditLog";

interface UseAuditLogDataOptions {
    sourceTypes: readonly AuditLogSourceType[];
    sourceId: number | string;
    enabled?: boolean;
    pageSize?: number;
}

type AuditLogFilterSubset = Pick<
    AuditLogFilter,
    "resourceType" | "changeType" | "actorType"
>;

interface UseAuditLogDataReturn {
    auditLogs: AuditLog[];
    isLoading: boolean;
    currentPage: number;
    totalPages: number;
    total: number;
    setCurrentPage: (page: number) => void;
    userFilters: AuditLogFilterSubset;
    userFiltersChangeHandler: (filters: AuditLogFilterSubset) => void;
    searchTerm: string;
    searchTermChangeHandler: (search: string) => void;
}

export function useAuditLogData({
    sourceTypes,
    sourceId,
    enabled = true,
    pageSize = 20,
}: UseAuditLogDataOptions): UseAuditLogDataReturn {
    const [userFilters, setUserFilters] = useState<AuditLogFilterSubset>(
        {} as AuditLogFilterSubset,
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const searchTermChangeHandler = useCallback((search: string) => {
        setSearchTerm(search);
        setCurrentPage(1);
    }, []);

    const userFiltersChangeHandler = useCallback(
        (filters: AuditLogFilterSubset) => {
            setUserFilters(filters);
            setCurrentPage(1);
        },
        [],
    );

    const filters = useMemo(
        () => ({
            sourceTypes: [...sourceTypes],
            sourceId: sourceId.toString(),
            ...userFilters,
            ...(searchTerm ? { search: searchTerm } : {}),
        }),
        [sourceTypes, sourceId, userFilters, searchTerm],
    );

    const { data: response, isLoading } = useGetAuditLogsQuery({
        filters,
        page: currentPage,
        limit: pageSize,
        options: {
            enabled,
        },
    });

    const auditLogs = response?.data ?? [];
    const totalPages = response?.totalPages ?? 0;
    const total = response?.total ?? 0;

    return {
        auditLogs,
        isLoading,
        currentPage,
        totalPages,
        total,
        setCurrentPage,
        userFilters,
        searchTerm,
        searchTermChangeHandler,
        userFiltersChangeHandler,
    };
}
